import { Box, Typography } from "@mui/material";
import HorizonCard from "./HorizonCard";
import StatusPanel from "./StatusPanel";
import ProductPanel from "./ProductPanel";
import PriorityPanel from "./PriorityPanel";
import { useLookups, useLookupColorMaps } from "../../hooks/useLookups";

export default function DashboardView({
  initiatives,
  user,
  presentationMode,
  onMetricClick,
}) {
  const { horizons, statuses, priorities, products } = useLookups();
  const horizonNames = horizons.map((h) => h.name);
  const statusNames = statuses.map((s) => s.name);
  const priorityNames = priorities.map((p) => p.name);
  const productNames = products.map((p) => p.name);
  const {
    horizonColors: horizonColorMap,
    statusColors: statusColorMap,
    priorityColors: priorityColorMap,
    productColors: productColorMap,
  } = useLookupColorMaps();

  const styles = {
    root: { p: 3 },
    greeting: { fontWeight: 600, mb: 2 },
    sectionLabel: {
      fontSize: "0.625rem",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      fontWeight: 700,
      color: "background.textMuted",
      mb: 1.25,
    },
    hzGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 1.5,
      mb: 3,
    },
    bottomGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 1.5,
    },
  };

  const horizonData = horizonNames.map((horizon) => {
    const items = initiatives.filter((i) => i.horizon === horizon);
    const avgProgress = items.length
      ? Math.round(
          items.reduce((s, i) => s + (i.progress || 0), 0) / items.length,
        )
      : 0;
    const statusMap = {};
    items.forEach((i) => {
      statusMap[i.status] = (statusMap[i.status] || 0) + 1;
    });
    return { horizon, count: items.length, avgProgress, statusMap };
  });

  const statusData = statusNames
    .map((status) => ({
      status,
      count: initiatives.filter((i) => i.status === status).length,
    }))
    .filter((d) => d.count > 0);

  const productCounts = {};
  initiatives.forEach((i) =>
    (i.product || []).forEach((p) => {
      productCounts[p] = (productCounts[p] || 0) + 1;
    }),
  );

  const productData = productNames
    .map((p) => ({
      product: p,
      count: productCounts[p] || 0,
    }))
    .filter((d) => d.count > 0)
    .sort((a, b) => b.count - a.count);

  const priorityData = priorityNames.map((priority) => ({
    priority,
    count: initiatives.filter((i) => i.priority === priority).length,
  }));

  return (
    <Box sx={styles.root}>
      {user?.idTokenClaims?.given_name && !presentationMode && (
        <Typography variant="h6" sx={styles.greeting}>
          Hello, {user.idTokenClaims.given_name}!
        </Typography>
      )}
      <Typography sx={styles.sectionLabel}>Horizon Breakdown</Typography>
      <Box sx={styles.hzGrid}>
        {horizonData.map(({ horizon, count, avgProgress, statusMap }) => (
          <HorizonCard
            key={horizon}
            horizon={horizon}
            count={count}
            avgProgress={avgProgress}
            statusMap={statusMap}
            horizonColorMap={horizonColorMap}
            statusColorMap={statusColorMap}
            onClick={() => onMetricClick("horizon", horizon)}
          />
        ))}
      </Box>

      <Typography sx={styles.sectionLabel}>Summary</Typography>
      <Box sx={styles.bottomGrid}>
        <StatusPanel
          statusData={statusData}
          total={initiatives.length}
          statusColorMap={statusColorMap}
          onMetricClick={onMetricClick}
        />
        <ProductPanel
          productData={productData}
          productColorMap={productColorMap}
          onMetricClick={onMetricClick}
        />
        <PriorityPanel
          priorityData={priorityData}
          priorityColorMap={priorityColorMap}
          onMetricClick={onMetricClick}
        />
      </Box>
    </Box>
  );
}
